import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal, Dropdown } from 'react-bootstrap'
import Select from 'react-select'

import DeleteModal from 'pages/parts/DeleteModal'
import { httpOk, GET, POST, PUT, DELETE } from 'helpers/api'
import { formatMoney } from 'helpers/helpers'
import HintIcon from 'pages/parts/HintIcon'

/** Длительность смены в минутах. Конец раньше начала — смена ночная, переходит на следующие сутки */
export function minutesBetween(startTime, endTime) {
	if (!startTime || !endTime) return 0

	const [startHours, startMinutes] = String(startTime).split(':').map(Number)
	const [endHours, endMinutes] = String(endTime).split(':').map(Number)
	if ([startHours, startMinutes, endHours, endMinutes].some((x) => Number.isNaN(x))) return 0

	const start = startHours * 60 + startMinutes
	const end = endHours * 60 + endMinutes

	return end > start ? end - start : (1440 - start) + end
}

export function minutesToHours(minutes) {
	return Math.round((Number(minutes || 0) / 60) * 100) / 100
}

function defaultItem() {
	return {
		name: '',
		comment: '',
		start_time: '09:00',
		end_time: '18:00',
		planned_minutes: 540,
		unpaid_minutes: 0,
		/** rate — по тарифу сотрудника, fixed — по ставке самой смены */
		payment_mode: 'rate',
		fixed_rate: '',
		pay_factor: 1,
		color: '#3b82f6',
		is_active: true,
		user_position_ids: [],
	}
}

function Index() {
	const { t } = useTranslation()

	const [data, setData] = useState([])
	const [positions, setPositions] = useState([])
	const [selectedItem, setSelectedItem] = useState(defaultItem())
	const [showModal, setShowModal] = useState(false)
	const [modalDelete, setModalDelete] = useState(false)
	const [deleteId, setDeleteId] = useState(0)

	async function searchWithFilter() {
		const response = await GET('/work-shift-types', {}, { loader: true })
		if (httpOk(response)) setData(response.data.data || [])
	}

	async function getPositions() {
		const response = await GET('/helper/employee-positions')
		if (httpOk(response)) setPositions(response.data)
	}

	async function createUpdate(e) {
		e.preventDefault()

		const payload = {
			...selectedItem,
			planned_minutes: Number(selectedItem.planned_minutes || 0),
			unpaid_minutes: Number(selectedItem.unpaid_minutes || 0),
			fixed_rate: selectedItem.payment_mode === 'fixed' ? Number(selectedItem.fixed_rate || 0) : null,
			pay_factor: Number(selectedItem.pay_factor || 1),
			user_position_ids: (selectedItem.user_position_ids || []).map((x) => Number(x)),
		}

		var response
		if (selectedItem.id) {
			response = await PUT(`/work-shift-types/${selectedItem.id}`, payload, { loader: true })
		} else {
			response = await POST('/work-shift-types', payload, { loader: true })
		}

		if (httpOk(response)) {
			searchWithFilter()
			setShowModal(false)
		}
	}

	async function deleteItem() {
		const response = await DELETE(`/work-shift-types/${deleteId}`, {}, { loader: true })
		if (httpOk(response)) {
			setModalDelete(false)
			setDeleteId(0)
			searchWithFilter()
		}
	}

	async function toggleModal(bool = false, item = null) {
		if (!bool) {
			setShowModal(false)
			return
		}

		if (!item) {
			setSelectedItem(defaultItem())
			setShowModal(true)
			return
		}

		// связи (должности) лежат отдельно — тянем карточку
		const response = await GET(`/work-shift-types/${item.id}`, {}, { loader: true })
		if (httpOk(response)) {
			setSelectedItem({
				...defaultItem(),
				...response.data,
				start_time: response.data.start_time ? String(response.data.start_time).substring(0, 5) : '',
				end_time: response.data.end_time ? String(response.data.end_time).substring(0, 5) : '',
				fixed_rate: response.data.fixed_rate ?? '',
				user_position_ids: response.data.user_position_ids || [],
			})
			setShowModal(true)
		}
	}

	/** Время смены поменяли — пересчитываем длительность, её всё равно можно поправить руками */
	function setShiftTime(field, value) {
		const item = { ...selectedItem, [field]: value }
		item.planned_minutes = minutesBetween(item.start_time, item.end_time) || item.planned_minutes
		setSelectedItem(item)
	}

	useEffect(() => {
		searchWithFilter()
		getPositions()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('work_shift_types')} [{data.length}]
				</h4>
				<div className="d-flex">
					<button className="btn-rounded btn btn-primary"
						onClick={() => toggleModal(true)}>
						{t('create')}
					</button>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '25%' }}>{t('name2')}</th>
									<th style={{ 'width': '15%' }} className="text-center">{t('shift_time')}</th>
									<th style={{ 'width': '10%' }} className="text-center">{t('shift_duration')}</th>
									<th style={{ 'width': '10%' }} className="text-center">{t('unpaid_break')}</th>
									<th style={{ 'width': '15%' }}>{t('payment_mode')}</th>
									<th style={{ 'width': '10%' }} className="text-center">{t('pay_factor')}</th>
									<th style={{ 'width': '15%' }}>{t('positions')}</th>
									<th style={{ 'minWidth': '100px' }} className="text-center">{t('status')}</th>
									<th style={{ 'minWidth': '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{data.map((item, index) => (
									<tr key={item.id || index}>
										<td className="text-center">{index + 1}</td>
										<td>
											<span className="shift-color-dot"
												style={{ 'backgroundColor': item.color || '#3b82f6' }}></span>
											{item.name}
										</td>
										<td className="text-center text-nowrap">
											{item.start_time ? String(item.start_time).substring(0, 5) : '—'}
											{item.end_time ? ` - ${String(item.end_time).substring(0, 5)}` : ''}
										</td>
										<td className="text-center">{minutesToHours(item.planned_minutes)} {t('hours')}</td>
										<td className="text-center">{item.unpaid_minutes || 0}</td>
										<td>
											{item.payment_mode === 'fixed'
												? `${t('payment_fixed')}: ${formatMoney(item.fixed_rate)}`
												: t('payment_by_rate')}
										</td>
										<td className="text-center">{item.pay_factor}</td>
										<td className="td-ellipsis" title={item.positions_names || ''}>
											{item.positions_names || t('all')}
										</td>
										<td className="text-center">
											{!item.is_active &&
												<span className="bg-danger bg-border"></span>
											}
											{item.is_active &&
												<span className="bg-primary bg-border"></span>
											}
										</td>
										<td>
											<div className="table-dropdown">
												<Dropdown id={`dropdown-${item.id || index}`}>
													<Dropdown.Toggle variant="outline-primary">
														<i className="uil uil-ellipsis-v"></i>
													</Dropdown.Toggle>

													<Dropdown.Menu>
														<div className="table-dropdown-item"
															onClick={() => toggleModal(true, item)}>
															<i className="uil-edit-alt"></i>
															{t('edit')}
														</div>
														<div className="table-dropdown-item text-danger"
															onClick={() => {
																setDeleteId(item.id)
																setModalDelete(true)
															}}>
															<i className="uil-times"></i>
															{t('delete')}
														</div>
													</Dropdown.Menu>
												</Dropdown>
											</div>
										</td>
									</tr>
								))}
								{!data.length &&
									<tr>
										<td colSpan={10} className="text-center text-muted">{t('list_empty')}</td>
									</tr>
								}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* MODAL */}
			<Modal show={showModal} animation={false} centered
				dialogClassName="update-modal-width" onHide={() => toggleModal()}>
				<Modal.Header closeButton>
					<Modal.Title>
						{!selectedItem.id ?
							<>{t('create')}</>
							:
							<>{t('edit')}</>
						}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={createUpdate} autoComplete="off">
						<div className="form-group">
							<label>{t('name2')}<span className="required-mark">*</span></label>
							<input type="text" className="form-control"
								value={selectedItem.name || ''}
								autoFocus
								onChange={(e) => setSelectedItem({ ...selectedItem, 'name': e.target.value })} />
						</div>

						<div className="row">
							<div className="form-group col-md-6">
								<label>{t('shift_start')}</label>
								<input type="time" className="form-control"
									value={selectedItem.start_time || ''}
									onChange={(e) => setShiftTime('start_time', e.target.value)} />
							</div>
							<div className="form-group col-md-6">
								<label>{t('shift_end')}</label>
								<input type="time" className="form-control"
									value={selectedItem.end_time || ''}
									onChange={(e) => setShiftTime('end_time', e.target.value)} />
							</div>
						</div>

						<div className="row">
							<div className="form-group col-md-6">
								<label>{t('shift_duration')} ({t('minutes')})<span className="required-mark">*</span></label>
								<input type="number" min="1" max="1440" className="form-control"
									value={selectedItem.planned_minutes ?? ''}
									onChange={(e) => setSelectedItem({ ...selectedItem, 'planned_minutes': e.target.value })} />
								<span className="text-muted small">
									{minutesToHours(selectedItem.planned_minutes)} {t('hours')}
								</span>
							</div>
							<div className="form-group col-md-6">
								<label>{t('unpaid_break')} ({t('minutes')})</label>
								<input type="number" min="0" max="1440" className="form-control"
									value={selectedItem.unpaid_minutes ?? 0}
									onChange={(e) => setSelectedItem({ ...selectedItem, 'unpaid_minutes': e.target.value })} />
							</div>
						</div>

						<div className="form-group">
							<label>{t('payment_mode')}</label>
							<div className="d-flex gap-3">
								<div className="form-check">
									<div className="radio-round"
										onClick={() => setSelectedItem({ ...selectedItem, 'payment_mode': 'rate' })}
										style={{ 'backgroundColor': selectedItem.payment_mode === 'rate' ? 'black' : '' }}
									/>
									<span onClick={() => setSelectedItem({ ...selectedItem, 'payment_mode': 'rate' })}>
										{t('payment_by_rate')}
									</span>
								</div>
								<div className="form-check">
									<div className="radio-round"
										onClick={() => setSelectedItem({ ...selectedItem, 'payment_mode': 'fixed' })}
										style={{ 'backgroundColor': selectedItem.payment_mode === 'fixed' ? 'black' : '' }}
									/>
									<span onClick={() => setSelectedItem({ ...selectedItem, 'payment_mode': 'fixed' })}>
										{t('payment_fixed')}
									</span>
								</div>
							</div>
						</div>

						{selectedItem.payment_mode === 'fixed' &&
							<div className="form-group">
								<label>{t('fixed_rate')}<span className="required-mark">*</span><HintIcon text={t('fixed_rate_hint')} /></label>
								<input type="number" step="0.01" min="0" className="form-control"
									value={selectedItem.fixed_rate ?? ''}
									onChange={(e) => setSelectedItem({ ...selectedItem, 'fixed_rate': e.target.value })} />
							</div>
						}

						<div className="row">
							<div className="form-group col-md-6">
								<label>{t('pay_factor')}<HintIcon text={t('pay_factor_hint')} /></label>
								<input type="number" step="0.01" min="0.01" max="10" className="form-control"
									value={selectedItem.pay_factor ?? 1}
									onChange={(e) => setSelectedItem({ ...selectedItem, 'pay_factor': e.target.value })} />
							</div>
							<div className="form-group col-md-6">
								<label>{t('color')}</label>
								<input type="color" className="form-control form-control-color"
									value={selectedItem.color || '#3b82f6'}
									onChange={(e) => setSelectedItem({ ...selectedItem, 'color': e.target.value })} />
							</div>
						</div>

						<div className="form-group">
							<label>{t('positions')}<HintIcon text={t('shift_positions_hint')} /></label>
							<Select
								isMulti
								isClearable
								options={positions}
								value={positions.filter((p) =>
									(selectedItem.user_position_ids || []).some((id) => Number(id) === Number(p.id))
								)}
								onChange={(options) => setSelectedItem({
									...selectedItem,
									'user_position_ids': options ? options.map((o) => Number(o.id)) : [],
								})}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>

						<div className="form-group">
							<label>{t('comment')}</label>
							<textarea className="form-control" rows="2"
								value={selectedItem.comment || ''}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'comment': e.target.value })} />
						</div>

						<div className="form-check">
							<input id="work-shift-type-active" className="form-check-input" type="checkbox"
								checked={selectedItem.is_active ?? true}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'is_active': e.target.checked })}
							/>
							<label className="form-check-label" htmlFor="work-shift-type-active">
								{t('active')}
							</label>
						</div>

						<div className="d-flex gap-2 w-100 mt-3">
							<button type="button" className="btn btn-outline-warning w-100"
								onClick={() => toggleModal()}>
								{t('cancel')}
							</button>
							<button type="submit" className="btn btn-primary w-100">
								{t('save')}
							</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>
			{/* MODAL */}

			<DeleteModal
				modalDelete={modalDelete}
				setModalDelete={setModalDelete}
				deleteItem={deleteItem}>
			</DeleteModal>
		</>
	)
}

export default Index
